import { config } from "@/config";
import { setUserFromToken } from "@/store/userStore";
export const authService = {
    async register(user) {
        const response = await fetch(`${config.API_URL}/api/Auth/register`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(user),
        });
        if (!response.ok) {
            const errorData = await response.json();
            // Maneja errores específicos según el código de estado
            if (response.status === 400 &&
                errorData.errores === "El email ya está en uso.") {
                throw new Error("El correo electrónico ya está en uso.");
            }
            throw new Error(errorData.errores || "Error en el registro");
        }
        const data = await response.json();
        return data;
    },
    async recoverPassword(email) {
        try {
            const response = await fetch(`${config.API_URL}/api/Auth/forgot-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                console.log(errorData);
                throw new Error(errorData.errores || "Error al enviar el correo de recuperación");
            }
            const data = await response.json();
            return data;
        }
        catch (error) {
            // Verifica si el error es un objeto de Error y muestra el mensaje apropiado
            if (error instanceof Error) {
                throw new Error(error.message || "Hubo un problema al recuperar la contraseña");
            }
            else if (error && error.errores) {
                // Si el error tiene la propiedad 'errores' (como la respuesta de la API)
                throw new Error(error.errores || "Hubo un problema al recuperar la contraseña");
            }
            else {
                throw new Error("Hubo un error desconocido al recuperar la contraseña");
            }
        }
    },
    // Nueva función para restablecer la contraseña
    async resetPassword(token, newPassword) {
        try {
            const response = await fetch(`${config.API_URL}/api/Auth/reset-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ token, newPassword }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Error al restablecer la contraseña");
            }
            const data = await response.json();
            return data;
        }
        catch (error) {
            throw new Error(error.message || "Hubo un problema al restablecer la contraseña");
        }
    },
    async login(credentials) {
        try {
            const response = await fetch(`${config.API_URL}/api/Auth/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(credentials),
            });
            // Verifica si la respuesta no es exitosa
            if (!response.ok) {
                const errorData = await response.json();
                // Lanza un error con el mensaje recibido del backend
                throw {
                    status: response.status,
                    message: errorData.errores || "Error en el inicio de sesión",
                };
            }
            const data = await response.json();
            const accessToken = data.datos.accessToken;
            const refreshToken = data.datos.refreshToken;
            // Almacena los tokens en localStorage
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
            // Decodifica y establece los datos del usuario desde el token
            setUserFromToken(accessToken);
            return data;
        }
        catch (error) {
            throw new Error(error.message || "Hubo un problema con el inicio de sesión.");
        }
    },
    logout() {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
    },
    getAccessToken() {
        return localStorage.getItem("accessToken");
    },
    async refreshToken() {
        const refreshToken = localStorage.getItem("refreshToken");
        const accessToken = localStorage.getItem("accessToken");
        if (!refreshToken) {
            throw new Error("No hay token de refresco");
        }
        const response = await fetch(`${config.API_URL}/api/Auth/refresh-token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ refreshToken, accessToken }),
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Error en el refresco del token");
        }
        const data = await response.json();
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        return data;
    },
    async requestVerificationCode(phoneNumber) {
        try {
            const response = await fetch(`${config.API_URL}/api/Auth/verify-phone`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ phoneNumber }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                console.log("Error service", errorData);
                throw new Error(errorData.errores || "Error al solicitar el código de verificación");
            }
            const data = await response.json();
            return data;
        }
        catch (error) {
            // Si se captura un error aquí, lo lanzamos hacia arriba
            console.error("Error in service:", error);
            throw error; // Esto pasará el error al frontend sin modificarlo
        }
    },
    async verifyCode(phoneNumber, verificationCode) {
        try {
            const response = await fetch(`${config.API_URL}/api/Auth/verify-phone-code`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ phoneNumber, verificationCode }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Código de verificación inválido");
            }
            const data = await response.json();
            return data;
        }
        catch (error) {
            throw new Error(error.message || "Hubo un problema al verificar el código");
        }
    },
    async activateAccount(token) {
        // Construir la URL con el token como parámetro de consulta
        const url = new URL(`${config.API_URL}/api/Auth/activate-account`);
        url.searchParams.append("token", token); // Agregar el token a la URL
        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (!response.ok) {
            let errorMessage = "Error al activar la cuenta.";
            try {
                const errorData = await response.json(); // Intentar parsear el error como JSON
                errorMessage = errorData?.errores || errorMessage;
            }
            catch {
                // Si no es un JSON válido, mantener el mensaje genérico
            }
            throw errorMessage;
        }
        // Manejar respuestas en texto o vacías
        const responseText = await response.text();
        return responseText ? JSON.parse(responseText) : {};
    }
};
